import { SkipMealBreakTile, TodaySchedule } from "../components";
import { ActionRequiredTile, DashboardTile, PunchTile, TimeOffTile, TimesheetSummaryTile } from "../components/mobile";
import {
    WebFlexWorkTile,
    WebPunchTile,
    WebTimeOffTile,
    WebTimesheetSummaryTile,
    WebWhoIsInTile,
} from "../components/web/webDashboard";
import ManagerDashboardTileContentWeb from "../components/mobile/m-timesheet/ManagerDashboardTileContent";
import ManagerDashboardTileContentMobile from "../components/mobile/m-timesheet/ManagerTimesheetDashTileContent";
import { BriefcaseBusiness } from "lucide-react";
import { WhoIsInTile } from "../pages/mobile/whoIsIn";
import { SchedulingToolMobileDashTile } from "../pages/schedulingTool";
import { ActionRequiredCount, ActorProfileMaster, DashboardConfigWidget } from "../types";

type DashboardWidgetVisibilityResolver = (widget: DashboardConfigWidget, fallbackVisibility?: boolean) => boolean;

type SharedDashboardWidgetProps = {
    widget: DashboardConfigWidget;
    isWidgetVisible: DashboardWidgetVisibilityResolver;
    skipMealBreakRefreshKey?: string | number;
    actionRequiredCount: ActionRequiredCount;
    isActionRequiredCountLoading: boolean;
};

type MobileDashboardWidgetProps = SharedDashboardWidgetProps & {
    whoIsWorkingEmp: ActorProfileMaster[];
    whoIsWorkingLoading: boolean;
    WHO_IS_IN?: boolean;
    TIMECARD?: boolean;
    MODULE_TIMEOFF?: boolean;
    MY_SCHEDULE?: boolean;
    REMOTE_WORK_COUNTER?: boolean;
    PARIS_WORK_COUNTER?: boolean;
    canShowTeamTimesheet: boolean;
    canShowShiftPlanner: boolean;
    onOpenWidgetUrl: (url: string) => void;
    onOpenSchedule: () => void;
    onOpenTeamTimesheet: () => void;
    onOpenShiftPlanner: () => void;
    onOpenTimeOff: () => void;
    onOpenNotifications: () => void;
};

type WebDashboardWidgetProps = SharedDashboardWidgetProps & {
    whoIsWorkingEmp: ActorProfileMaster[];
    whoIsWorkingLoading: boolean;
    WHO_IS_IN?: boolean;
    TIMECARD?: boolean;
    MODULE_TIMEOFF?: boolean;
    MY_SCHEDULE?: boolean;
    REMOTE_WORK_COUNTER?: boolean;
    PARIS_WORK_COUNTER?: boolean;
    canShowTeamTimesheet: boolean;
    canShowTimecardWidget: boolean;
    canShowShiftPlanner: boolean;
    onOpenWidgetUrl: (url: string) => void;
    onOpenSchedule: () => void;
    onOpenTeamTimesheet: () => void;
    onOpenShiftPlanner: () => void;
    onOpenTimeOff: () => void;
    onOpenNotifications: () => void;
};

type MobileDashboardWidgetRenderer = (props: MobileDashboardWidgetProps) => React.ReactNode;
type WebDashboardWidgetRenderer = (props: WebDashboardWidgetProps) => React.ReactNode;

type ShiftPlannerWidgetProps = Pick<
    MobileDashboardWidgetProps,
    "widget" | "isWidgetVisible" | "canShowShiftPlanner" | "onOpenShiftPlanner"
>;

type RemoteWorkCounterWidgetProps = Pick<
    MobileDashboardWidgetProps,
    "widget" | "isWidgetVisible" | "REMOTE_WORK_COUNTER"
>;

type ParisWorkCounterWidgetProps = Pick<
    MobileDashboardWidgetProps,
    "widget" | "isWidgetVisible" | "PARIS_WORK_COUNTER"
>;

const isAbsoluteUrl = (url: string): boolean => /^[a-z][a-z\d+\-.]*:\/\//i.test(url);

const openDashboardWidgetLink = (
    widget: DashboardConfigWidget,
    onOpenWidgetUrl: (url: string) => void,
    fallbackNavigate?: () => void
): void => {
    if (!widget.url) {
        fallbackNavigate?.();
        return;
    }

    if (widget.target === "_blank") {
        window.open(widget.url, "_blank", "noopener,noreferrer");
        return;
    }

    if (isAbsoluteUrl(widget.url)) {
        window.location.assign(widget.url);
        return;
    }

    onOpenWidgetUrl(widget.url);
};

const MealBreakWidget = ({ widget, isWidgetVisible, skipMealBreakRefreshKey }: SharedDashboardWidgetProps): React.ReactNode => {
    if (!isWidgetVisible(widget, true)) {
        return null;
    }

    return <SkipMealBreakTile refreshKey={skipMealBreakRefreshKey} />;
};

const ActionRequiredWidget: MobileDashboardWidgetRenderer = ({
    widget,
    isWidgetVisible,
    onOpenNotifications,
    actionRequiredCount,
    isActionRequiredCountLoading,
}) => {
    if (!isWidgetVisible(widget, true)) {
        return null;
    }

    return (
        <ActionRequiredTile
            count={actionRequiredCount}
            isLoading={isActionRequiredCountLoading}
            onNavigate={onOpenNotifications}
        />
    );
};

const MobileWhoIsInWidget: MobileDashboardWidgetRenderer = ({
    widget,
    isWidgetVisible,
    WHO_IS_IN,
    whoIsWorkingEmp,
    whoIsWorkingLoading,
}) => {
    if (!isWidgetVisible(widget, WHO_IS_IN)) {
        return null;
    }

    return <WhoIsInTile employees={whoIsWorkingEmp} isLoading={whoIsWorkingLoading} />;
};

const MobileMyTimesheetWidget: MobileDashboardWidgetRenderer = ({ widget, isWidgetVisible, TIMECARD }) => {
    if (!isWidgetVisible(widget, TIMECARD)) {
        return null;
    }

    return <TimesheetSummaryTile />;
};

const MobileTeamTimesheetWidget: MobileDashboardWidgetRenderer = ({
    widget,
    isWidgetVisible,
    canShowTeamTimesheet,
    onOpenWidgetUrl,
    onOpenTeamTimesheet,
}) => {
    if (!isWidgetVisible(widget, canShowTeamTimesheet)) {
        return null;
    }

    return (
        <DashboardTile
            title="Team Timesheets"
            onNavigate={() => openDashboardWidgetLink(widget, onOpenWidgetUrl, onOpenTeamTimesheet)}
            children={<ManagerDashboardTileContentMobile />}
        />
    );
};

const MobileTimeOffWidget: MobileDashboardWidgetRenderer = ({
    widget,
    isWidgetVisible,
    MODULE_TIMEOFF,
    onOpenWidgetUrl,
    onOpenTimeOff,
}) => {
    if (!isWidgetVisible(widget, MODULE_TIMEOFF)) {
        return null;
    }

    return <TimeOffTile onNavigate={() => openDashboardWidgetLink(widget, onOpenWidgetUrl, onOpenTimeOff)} />;
};

const MobileScheduleWidget: MobileDashboardWidgetRenderer = ({
    widget,
    isWidgetVisible,
    MY_SCHEDULE,
    onOpenWidgetUrl,
    onOpenSchedule,
}) => {
    if (!isWidgetVisible(widget, MY_SCHEDULE)) {
        return null;
    }

    return (
        <DashboardTile
            title="Work Schedule & Availability"
            onNavigate={() => openDashboardWidgetLink(widget, onOpenWidgetUrl, onOpenSchedule)}
            children={<TodaySchedule onPress={() => openDashboardWidgetLink(widget, onOpenWidgetUrl, onOpenSchedule)} />}
        />
    );
};

const MobileTimecardWidget: MobileDashboardWidgetRenderer = ({ widget, isWidgetVisible, TIMECARD }) => {
    if (!isWidgetVisible(widget, TIMECARD)) {
        return null;
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <PunchTile />
        </div>
    );
};

const ShiftPlannerWidget = ({
    widget,
    isWidgetVisible,
    canShowShiftPlanner,
    onOpenWidgetUrl,
    onOpenShiftPlanner,
}: ShiftPlannerWidgetProps & Pick<MobileDashboardWidgetProps, "onOpenWidgetUrl">): React.ReactNode => {
    if (!isWidgetVisible(widget, canShowShiftPlanner)) {
        return null;
    }

    return (
        <DashboardTile
            title="Shift Planner"
            onNavigate={() => openDashboardWidgetLink(widget, onOpenWidgetUrl, onOpenShiftPlanner)}
            children={<SchedulingToolMobileDashTile onPress={() => openDashboardWidgetLink(widget, onOpenWidgetUrl, onOpenShiftPlanner)} />}
        />
    );
};

const RemoteWorkCounterWidget = ({
    widget,
    isWidgetVisible,
    REMOTE_WORK_COUNTER,
}: RemoteWorkCounterWidgetProps): React.ReactNode => {
    if (!isWidgetVisible(widget, REMOTE_WORK_COUNTER)) {
        return null;
    }

    const maxRemoteDays = widget.config?.max_remote_days ?? 60;

    return (
        <DashboardTile
            title="Remote Work"
            showNavigationArrow={false}
            children={
                <WebFlexWorkTile
                    fieldCode="RDU"
                    totalDays={maxRemoteDays}
                    variant="remote-work"
                    progressColors={{
                        low: "bg-blue-500",
                        medium: "bg-yellow-500",
                        high: "bg-red-500",
                    }}
                    loaderColor="text-blue-500"
                    startDate="Jan 01, 2026"
                />
            }
        />
    );
};

const ParisWorkCounterWidget = ({
    widget,
    isWidgetVisible,
    PARIS_WORK_COUNTER,
}: ParisWorkCounterWidgetProps): React.ReactNode => {
    if (!isWidgetVisible(widget, PARIS_WORK_COUNTER)) {
        return null;
    }

    return (
        <DashboardTile
            title="Forfait Jours"
            showNavigationArrow={false}
            children={
                <WebFlexWorkTile
                    fieldCode="pariswork"
                    totalDays={218}
                    variant="forfait-jours"
                    progressColors={{
                        low: "bg-blue-500",
                        medium: "bg-yellow-500",
                        high: "bg-red-500",
                    }}
                    loaderColor="text-blue-500"
                />
            }
        />
    );
};

const ProjectLaborTimesheetTileContent: React.FC = () => {
    return (
        <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <BriefcaseBusiness className="h-5 w-5" />
            </span>
            <p className="text-sm text-gray-500">
                Open project labor timesheet
            </p>
        </div>
    );
};

const ProjectLaborTimesheetWidget: MobileDashboardWidgetRenderer = ({
    widget,
    isWidgetVisible,
    onOpenWidgetUrl,
}) => {
    if (!isWidgetVisible(widget, true)) {
        return null;
    }

    return (
        <DashboardTile
            title={widget.title || "Project Labor Timesheet"}
            onNavigate={() => openDashboardWidgetLink(widget, onOpenWidgetUrl)}
            children={<ProjectLaborTimesheetTileContent />}
        />
    );
};

const WebWhoIsInWidget: WebDashboardWidgetRenderer = ({
    widget,
    isWidgetVisible,
    WHO_IS_IN,
    whoIsWorkingEmp,
    whoIsWorkingLoading,
}) => {
    if (!isWidgetVisible(widget, WHO_IS_IN)) {
        return null;
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
            <WebWhoIsInTile employees={whoIsWorkingEmp} isLoading={whoIsWorkingLoading} />
        </div>
    );
};

const WebMyTimesheetWidget: WebDashboardWidgetRenderer = ({ widget, isWidgetVisible, TIMECARD }) => {
    if (!isWidgetVisible(widget, TIMECARD)) {
        return null;
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
            <WebTimesheetSummaryTile />
        </div>
    );
};

const WebTeamTimesheetWidget: WebDashboardWidgetRenderer = ({
    widget,
    isWidgetVisible,
    canShowTeamTimesheet,
    onOpenWidgetUrl,
    onOpenTeamTimesheet,
}) => {
    if (!isWidgetVisible(widget, canShowTeamTimesheet)) {
        return null;
    }

    return (
        <DashboardTile
            title="Team Timesheets"
            onNavigate={() => openDashboardWidgetLink(widget, onOpenWidgetUrl, onOpenTeamTimesheet)}
            children={<ManagerDashboardTileContentWeb />}
        />
    );
};

const WebTimeOffWidget: WebDashboardWidgetRenderer = ({
    widget,
    isWidgetVisible,
    MODULE_TIMEOFF,
    onOpenWidgetUrl,
    onOpenTimeOff,
}) => {
    if (!isWidgetVisible(widget, MODULE_TIMEOFF)) {
        return null;
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
            <WebTimeOffTile onNavigate={() => openDashboardWidgetLink(widget, onOpenWidgetUrl, onOpenTimeOff)} />
        </div>
    );
};

const WebScheduleWidget: WebDashboardWidgetRenderer = ({
    widget,
    isWidgetVisible,
    MY_SCHEDULE,
    onOpenWidgetUrl,
    onOpenSchedule,
}) => {
    if (!isWidgetVisible(widget, MY_SCHEDULE)) {
        return null;
    }

    return (
        <DashboardTile
            title="Work Schedule & Availability"
            onNavigate={() => openDashboardWidgetLink(widget, onOpenWidgetUrl, onOpenSchedule)}
            children={<TodaySchedule onPress={() => openDashboardWidgetLink(widget, onOpenWidgetUrl, onOpenSchedule)} />}
        />
    );
};

const WebTimecardWidget: WebDashboardWidgetRenderer = ({
    widget,
    isWidgetVisible,
    canShowTimecardWidget,
}) => {
    if (!canShowTimecardWidget || !isWidgetVisible(widget, true)) {
        return null;
    }

    return (
        <div className="bg-white rounded-xl border border-gray-200">
            <WebPunchTile />
        </div>
    );
};

export const mobileDashboardWidgetComponents: Record<string, MobileDashboardWidgetRenderer> = {
    action_required_count: ActionRequiredWidget,
    action_required: ActionRequiredWidget,
    skip_meal_break: MealBreakWidget,
    meal_break: MealBreakWidget,
    who_is_in: MobileWhoIsInWidget,
    my_timesheet: MobileMyTimesheetWidget,
    team_timesheet: MobileTeamTimesheetWidget,
    module_timeoff: MobileTimeOffWidget,
    my_schedule: MobileScheduleWidget,
    shift_planner: ShiftPlannerWidget,
    remote_work_counter: RemoteWorkCounterWidget,
    paris_work_counter: ParisWorkCounterWidget,
    project_labor_timesheet: ProjectLaborTimesheetWidget,
    webclock: MobileTimecardWidget,
    timecard: MobileTimecardWidget,
};

export const webDashboardWidgetComponents: Record<string, WebDashboardWidgetRenderer> = {
    action_required_count: ActionRequiredWidget,
    action_required: ActionRequiredWidget,
    skip_meal_break: MealBreakWidget,
    meal_break: MealBreakWidget,
    who_is_in: WebWhoIsInWidget,
    my_timesheet: WebMyTimesheetWidget,
    team_timesheet: WebTeamTimesheetWidget,
    module_timeoff: WebTimeOffWidget,
    my_schedule: WebScheduleWidget,
    shift_planner: ShiftPlannerWidget,
    remote_work_counter: RemoteWorkCounterWidget,
    paris_work_counter: ParisWorkCounterWidget,
    project_labor_timesheet: ProjectLaborTimesheetWidget,
    webclock: WebTimecardWidget,
    timecard: WebTimecardWidget,
};

export const renderMobileDashboardWidget = (props: MobileDashboardWidgetProps): React.ReactNode => {
    const renderWidget = props.widget.widget_code ? mobileDashboardWidgetComponents[props.widget.widget_code] : undefined;

    if (!renderWidget) {
        return null;
    }

    return renderWidget(props);
};

export const renderWebDashboardWidget = (props: WebDashboardWidgetProps): React.ReactNode => {
    const renderWidget = props.widget.widget_code ? webDashboardWidgetComponents[props.widget.widget_code] : undefined;

    if (!renderWidget) {
        return null;
    }

    return renderWidget(props);
};
